"use client";

import { Icon } from "@ospweb/sdk-ui";
import { useSnapshot } from "valtio";
import { cn } from "utils";

import { inviteCodeState } from "@utils/invite/inviteCodeStore";
import { createInviteCodeCopy } from "./inviteCodeCopy";

/**
 * 登录/注册页底部的邀请码入口行 —— 两种状态占同一位置（Figma y=473）：
 *
 * - **已有码**（`104980:320673`）：`✓ Referral code <code> applied`，code 段 semibold
 * - **无码**（`I102803:53623;104980:319962`）：`Have a referral code?`
 *
 * 两态都可点击 → 邀请码填写页（已有码时进去是修改预填值）。
 *
 * 两端同源：h5 与原生用同一份视觉规格，PC 仅宽度不同（父容器决定），故本组件不含端判断。
 */
interface InviteCodeEntryRowProps {
  /** 点击后跳填写页；由调用方决定用路由跳转（h5）还是打开 Modal（PC）。 */
  onClick: () => void;
  className?: string;
  locale?: string;
}

export function InviteCodeEntryRow({
  onClick,
  className,
  locale,
}: InviteCodeEntryRowProps) {
  const { code, applied } = useSnapshot(inviteCodeState);
  const copy = createInviteCodeCopy(locale);

  // 有码：两种展示态共用同一行结构（Figma 104980:320673），只换 icon 与颜色
  if (code) {
    // applied === false → 后端**没识别到**。无效码 / 429 限流 / 网络失败统一走这一态：
    // 用户只需知道「码传进来了但没生效」，不必区分原因（也无法据此自助修复）。
    // applied === null（尚未校验完）按乐观态展示，避免先闪一下"未生效"。
    const recognized = applied !== false;
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={
          recognized ? "Referral code applied" : "Referral code not applied"
        }
        data-openreplay-label={
          recognized
            ? "login_invitecode_applied_click"
            : "login_invitecode_notapplied_click"
        }
        data-testid={
          recognized ? "invite-code-applied-row" : "invite-code-not-applied-row"
        }
        className={cn(
          "flex w-full items-center justify-center gap-1",
          recognized ? "text-content_brand_primary" : "text-content_tertiary",
          className,
        )}
      >
        <Icon
          name={recognized ? "check" : "error-circle-regular"}
          size={20}
          className="shrink-0"
        />
        <span className="button_m">
          {copy.appliedPrefix}
          <span className="body_bold_m">{code}</span>
          {recognized ? copy.appliedSuffix : copy.notAppliedSuffix}
        </span>
      </button>
    );
  }

  // 无码：「Have a referral code?」（Figma I102803:53623;104980:319962）
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Have a referral code"
      data-openreplay-label="login_invitecode_entry_click"
      data-testid="invite-code-entry-row"
      className={cn(
        "button_m w-full text-center text-content_brand_primary",
        className,
      )}
    >
      {copy.haveACode}
    </button>
  );
}
